import { apiClient } from './client';
import type { ApiResponse } from '../types';

export type CivilisationTaskType = 'explore' | 'research' | 'conserve' | 'trade' | 'defend' | 'culture';
export type CivilisationDoctrine = 'explorer' | 'trade' | 'peace' | 'defence' | 'research' | 'dominion';

export interface CivilisationPersonality {
  intelligence: number;
  war: number;
  trade: number;
  exploration: number;
  conservation: number;
  spirituality: number;
  industry: number;
  diplomacy: number;
}

export interface CivilisationState {
  ambassador?: {
    baseSpecies: string;
    evolvedTitle: string;
    trait: string;
    createdDay: number;
  };
  empire?: {
    name?: string;
    namedAt?: number;
    locked?: boolean;
  };
  personality: CivilisationPersonality;
  tasks: {
    active?: {
      taskId: string;
      type: CivilisationTaskType;
      label: string;
      startedDay: number;
      durationDays: number;
      remainingDays: number;
    } | null;
    history: Array<{
      taskId: string;
      type: CivilisationTaskType;
      label: string;
      startedDay: number;
      completedDay: number;
    }>;
  };
  religion?: {
    name: string;
    formedDay: number;
    basis: string;
  } | null;
  lawBiases: Record<string, number>;
  problemRisks: Record<string, number>;
}

export interface CivilisationResponse {
  unlocked: boolean;
  unlockReason: string | null;
  civilisation: CivilisationState;
  stats: Record<string, number>;
  spaceDoctrine?: CivilisationDoctrine | null;
  factionGoal?: {
    type: string;
    label: string;
    reason: string;
  } | null;
  score?: number;
  scoreBreakdown?: Record<string, number>;
  recentScoreEvents?: Array<{
    reason: string;
    points: number;
    category: string;
    createdDay?: number | null;
  }>;
  lore?: Partial<Record<'civilisation' | 'ambassador' | 'empire' | 'religion', {
    text: string;
    source: string;
    model?: string | null;
    createdDay?: number | null;
  }>>;
  planetState?: Record<string, unknown>;
  currentDay: number;
}

export const civilisationApi = {
  getCivilisation: () =>
    apiClient.get<ApiResponse<CivilisationResponse>>('/civilisation').then((r) => r.data),

  startTask: (taskType: CivilisationTaskType) =>
    apiClient.post<ApiResponse<{ civilisation: CivilisationState }>>('/civilisation/tasks', { taskType }).then((r) => r.data),

  nameEmpire: (name: string) =>
    apiClient.post<ApiResponse<{ civilisation: CivilisationState }>>('/civilisation/empire-name', {
      name,
      confirmLocked: true,
    }).then((r) => r.data),

  setDoctrine: (doctrine: CivilisationDoctrine) =>
    apiClient.post<ApiResponse<{ civilisation: CivilisationState; spaceDoctrine: CivilisationDoctrine; factionGoal: CivilisationResponse['factionGoal'] }>>(
      '/civilisation/doctrine',
      { doctrine },
    ).then((r) => r.data),

  dialogue: (promptId: string) =>
    apiClient.post<ApiResponse<{ promptId: string; message: string }>>('/civilisation/dialogue', { promptId }).then((r) => r.data),
};
